import React, { useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CARD_BG } from "../config/theme";
import {
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  Code,
  Heading1,
  Heading2,
} from "lucide-react";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Markdown editor with toolbar and live preview.
 * Supports bold, italic, lists, links, code, and headings.
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = "Start typing...",
  className = "",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = React.useState(false);

  // Insert markdown syntax at cursor position
  const insertMarkdown = (before: string, after: string = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const beforeText = value.substring(0, start);
    const afterText = value.substring(end);

    const newText = `${beforeText}${before}${selectedText}${after}${afterText}`;
    onChange(newText);

    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Toolbar button handlers
  const makeBold = () => insertMarkdown("**", "**");
  const makeItalic = () => insertMarkdown("_", "_");
  const makeHeading1 = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const beforeText = value.substring(0, start);
    const lineStart = beforeText.lastIndexOf("\n") + 1;
    const newText =
      value.substring(0, lineStart) + "# " + value.substring(lineStart);
    onChange(newText);
  };
  const makeHeading2 = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const beforeText = value.substring(0, start);
    const lineStart = beforeText.lastIndexOf("\n") + 1;
    const newText =
      value.substring(0, lineStart) + "## " + value.substring(lineStart);
    onChange(newText);
  };
  const makeList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const beforeText = value.substring(0, start);
    const lineStart = beforeText.lastIndexOf("\n") + 1;
    const newText =
      value.substring(0, lineStart) + "• " + value.substring(lineStart);
    onChange(newText);
  };
  const makeLink = () => insertMarkdown("[", "](url)");
  const makeCode = () => insertMarkdown("`", "`");

  return (
    <div className={className}>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-t-lg">
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={makeBold}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Bold (Ctrl+B)"
          >
            <Bold className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
          <button
            type="button"
            onClick={makeItalic}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Italic (Ctrl+I)"
          >
            <Italic className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          <button
            type="button"
            onClick={makeHeading1}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
          <button
            type="button"
            onClick={makeHeading2}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          <button
            type="button"
            onClick={makeList}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Bullet List"
          >
            <List className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
          <button
            type="button"
            onClick={makeLink}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Insert Link"
          >
            <LinkIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
          <button
            type="button"
            onClick={makeCode}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Inline Code"
          >
            <Code className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {showPreview ? "Edit" : "Preview"}
        </button>
      </div>

      {/* Editor / Preview */}
      {showPreview ? (
        <div
          className={`min-h-[200px] px-4 py-3 border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-lg ${CARD_BG}
                     prose prose-sm dark:prose-invert max-w-none
                     prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-white
                     prose-p:text-gray-700 dark:prose-p:text-gray-300
                     prose-strong:text-gray-900 dark:prose-strong:text-white
                     prose-em:text-gray-700 dark:prose-em:text-gray-300
                     prose-code:text-blue-600 dark:prose-code:text-blue-400
                     prose-code:bg-blue-50 dark:prose-code:bg-blue-900/20
                     prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                     prose-a:text-blue-600 dark:prose-a:text-blue-400`}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {value || "*No content to preview*"}
          </ReactMarkdown>
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full min-h-[200px] px-4 py-3 border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-lg
                   ${CARD_BG} text-gray-900 dark:text-white
                   placeholder-gray-400 dark:placeholder-gray-500
                   focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y
                   font-mono text-sm`}
          onKeyDown={(e) => {
            // Keyboard shortcuts
            if (e.ctrlKey || e.metaKey) {
              if (e.key === "b") {
                e.preventDefault();
                makeBold();
              } else if (e.key === "i") {
                e.preventDefault();
                makeItalic();
              }
            }
          }}
        />
      )}

      {/* Helper text */}
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium">Markdown supported:</span> **bold**,
        _italic_, # heading, • bullet, [link](url), `code`
      </div>
    </div>
  );
};
